import { CollectionResponse } from "@rumble-it/common-lib/types/api/CollectionResponse.interface";
import { axiosInstance } from "../client";
import { Agent, AiConnection } from "./agents";

/**
 * Lightweight agent summary embedded on BenchmarkRun/BenchmarkConfig (mirrors backend
 * AgentSummaryOutput): richer than a bare IRI so the cockpit can read `agent.name`/
 * `agent.aiConnection` off the embedded object without a second request.
 */
export interface BenchmarkAgentSummary {
  id: string;
  name: string;
  aiConnection: AiConnection | null;
}

export interface BenchmarkRun {
  id: string;
  status: string;
  statusMessage: string | null;
  totalCases: number;
  completedCases: number;
  failedCases: number;
  totalCost: number | null;
  totalTokens: number | null;
  averageLatencyMs: number | null;
  p95LatencyMs: number | null;
  averageQualityScore: number | null;
  accuracyRate: number | null;
  failureRate: number | null;
  createdAt: string;
  ruleActionId: string | null;
  config: Record<string, unknown> | null;
  agent: BenchmarkAgentSummary;
  advice?: string | null;
  benchmarkConfig?: {
    id: string;
    name: string;
  } | null;
}

export interface BenchmarkResult {
  id: string;
  inputReference: string | null;
  actualOutput: Record<string, unknown> | null;
  expectedOutput: Record<string, unknown> | null;
  correct: boolean | null;
  qualityScore: number | null;
  judgeReasoning: string | null;
  latencyMs: number | null;
  tokensUsed: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  cost: number | null;
  createdAt: string;
  testCase: {
    id: string;
    name: string;
  } | null;
}

export const fetchBenchmarkRuns = async (
  page = 1,
  sortBy = "createdAt",
  sortOrder = "desc",
  searchTerm = "",
  filters: Record<string, any> = {},
): Promise<CollectionResponse<BenchmarkRun>> => {
  let queryStr = searchTerm
    ? `&benchmarkConfig.name=${encodeURIComponent(searchTerm)}`
    : "";

  if (filters.status && filters.status.length > 0) {
    // API Platform array filter syntax or just exact match for multiple?
    // Usually it's ?status[]=running&status[]=failed, but standard exact match works if single.
    filters.status.forEach((s: string) => {
      queryStr += `&status[]=${encodeURIComponent(s)}`;
    });
  }

  return axiosInstance
    .get(
      `/benchmark-runs?page=${page}&order[${sortBy}]=${sortOrder}${queryStr}`,
    )
    .then((response) => response.data);
};

export const fetchBenchmarkRunCountForConfig = async (
  configId: string,
): Promise<number> => {
  return axiosInstance
    .get(`/benchmark-runs?benchmarkConfig=${configId}&page=1`)
    .then((response) => response.data["hydra:totalItems"] || 0);
};

export const fetchBenchmarkRun = async (
  runId: string,
): Promise<BenchmarkRun> => {
  return axiosInstance
    .get(`/benchmark-runs/${runId}`)
    .then((response) => response.data);
};

export const fetchBenchmarkRunResults = async (
  runId: string,
): Promise<CollectionResponse<BenchmarkResult>> => {
  const id = runId.split("/").pop();
  return axiosInstance
    .get(`/benchmark-runs/${id}/results?page=1`)
    .then((response) => response.data);
};

// Unwrap a JSON-LD/Hydra collection (or a plain array) into a flat array. Shared so the
// compare feature does not add yet another copy of this pattern.
export const unwrapCollection = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  const d = data as { member?: T[]; "hydra:member"?: T[] } | null | undefined;
  return d?.member || d?.["hydra:member"] || [];
};

const COMPARE_PAGE_SIZE = 2000;

// Fetch every result of a run for comparison in as few requests as possible. The server caps
// the page size (see BenchmarkResult::GetCollection), so one request normally suffices; we loop
// defensively for very large runs and hard-cap the loop so a misbehaving API can never hang the UI.
export const fetchCompactRunResults = async (
  runId: string,
): Promise<BenchmarkResult[]> => {
  const id = runId.split("/").pop();
  const out: BenchmarkResult[] = [];
  for (let page = 1; page <= 25; page += 1) {
    const data = await axiosInstance
      .get(
        `/benchmark-runs/${id}/results?itemsPerPage=${COMPARE_PAGE_SIZE}&page=${page}&order[createdAt]=asc`,
      )
      .then((response) => response.data);
    const members = unwrapCollection<BenchmarkResult>(data);
    out.push(...members);
    if (members.length < COMPARE_PAGE_SIZE) break;
  }
  return out;
};

export const fetchBenchmarkResultFile = async (
  resultId: string,
): Promise<Blob> => {
  const id = resultId.split("/").pop();
  return axiosInstance
    .get(`/benchmark-results/${id}/file`, { responseType: "blob" })
    .then((response) => response.data);
};

export interface BenchmarkRunPayload {
  agent: string;
  ruleAction?: string | null;
  config?: Record<string, unknown> | null;
  benchmarkConfig?: string;
}

export const triggerBenchmarkRun = async (
  agent: string,
  ruleActionId?: string,
  config?: Record<string, unknown>,
  benchmarkConfig?: string,
): Promise<BenchmarkRun> => {
  const data: BenchmarkRunPayload = {
    agent,
  };

  if (ruleActionId) {
    data.ruleAction = ruleActionId;
  }

  if (config) {
    data.config = config;
  }

  if (benchmarkConfig) {
    data.benchmarkConfig = benchmarkConfig;
  }

  return axiosInstance
    .post(`/benchmark-runs`, data)
    .then((response) => response.data);
};

export const deleteBenchmarkRun = async (runId: string): Promise<void> => {
  return axiosInstance
    .delete(`/benchmark-runs/${runId}`)
    .then((response) => response.data);
};

export const abortBenchmarkRun = async (runId: string): Promise<void> => {
  const id = runId.split("/").pop();
  return axiosInstance
    .post(`/benchmark-runs/${id}/abort`)
    .then((response) => response.data);
};

export const promoteBenchmarkRun = async (
  runId: string,
  resultIds: string[],
): Promise<any> => {
  return axiosInstance
    .post(`/benchmark-runs/${runId}/promote`, { resultIds })
    .then((response) => response.data);
};

export const deleteBenchmarkRunGroundTruth = async (
  runId: string,
): Promise<any> => {
  return axiosInstance
    .delete(`/benchmark-runs/${runId}/ground-truth`)
    .then((response) => response.data);
};

export const fetchAgents = async (): Promise<CollectionResponse<Agent>> => {
  return axiosInstance.get(`/agents?page=1`).then((response) => response.data);
};

/**
 * One supported LLM provider and the models its symfony/ai bridge knows about, used to offer a
 * friendly model picker per provider. `models` is empty for self-hosted providers (e.g. Ollama),
 * whose available models depend on the user's own server — those rely on free-text entry.
 */
export interface AgentModelCatalogEntry {
  provider: string;
  label: string;
  requiresExplicitHost: boolean;
  models: AgentModelInfo[];
}

/**
 * A single model known to a provider's bridge catalog, with the capability identifiers the catalog
 * advertises for it (the symfony/ai `Capability` values, e.g. `output-text`, `input-image`). Filter
 * on these for the use case at hand (e.g. `output-text` for the chat picker, `input-image` for image
 * judging).
 */
export interface AgentModelInfo {
  name: string;
  capabilities: string[];
}

export const fetchAgentModelCatalog = async (): Promise<
  AgentModelCatalogEntry[]
> => {
  return axiosInstance
    .get(`/agent-models`)
    .then((response) =>
      unwrapCollection<AgentModelCatalogEntry>(response.data),
    );
};

export interface RuleAction {
  id: string;
  ruleActionType: string;
  ruleActionTypeValue: string;
  name: string;
  data: any;
}

export const fetchRuleActions = async (): Promise<
  CollectionResponse<RuleAction>
> => {
  return axiosInstance
    .get(`/admin/rules/actions`)
    .then((response) => response.data);
};

export interface BenchmarkFolderDto {
  id: string;
  name: string;
  count: number;
}

/**
 * Mirrors the backend BenchmarkConfigOutput. The embedded `agent` is an AgentSummaryOutput (not a
 * bare IRI); `createdAt` is always present on read.
 */
export interface BenchmarkConfig {
  id: string;
  name: string;
  agent: BenchmarkAgentSummary;
  ruleActionId: string | null;
  config: Record<string, unknown> | null;
  createdAt: string;
}

/**
 * Write payload for creating/updating a BenchmarkConfig (mirrors backend BenchmarkConfigInput):
 * `agent` and `ruleAction` are sent as bare UUIDs.
 */
export interface BenchmarkConfigPayload {
  name: string;
  agent: string;
  ruleAction?: string | null;
  config?: Record<string, unknown> | null;
}

export const fetchBenchmarkConfigs = async (
  page = 1,
  sortBy = "createdAt",
  sortOrder = "desc",
  searchTerm = "",
): Promise<CollectionResponse<BenchmarkConfig>> => {
  const searchFilter = searchTerm
    ? `&name=${encodeURIComponent(searchTerm)}`
    : "";
  return axiosInstance
    .get(
      `/benchmark-configs?page=${page}&order[${sortBy}]=${sortOrder}${searchFilter}`,
    )
    .then((response) => response.data);
};

export const createBenchmarkConfig = async (
  payload: BenchmarkConfigPayload,
): Promise<BenchmarkConfig> => {
  return axiosInstance
    .post("/benchmark-configs", payload)
    .then((response) => response.data);
};

export const updateBenchmarkConfig = async (
  id: string,
  payload: Partial<BenchmarkConfigPayload>,
): Promise<BenchmarkConfig> => {
  return axiosInstance
    .put(`/benchmark-configs/${id}`, payload)
    .then((response) => response.data);
};

export const deleteBenchmarkConfig = async (id: string): Promise<void> => {
  return axiosInstance.delete(`/benchmark-configs/${id}`);
};

export interface BenchmarkInputFolders {
  type: string;
  folders: BenchmarkFolderDto[];
}

// Lists, per folder-based input source (image_folder, eml_folder), the folders available on the server
// so the wizard can offer them for selection instead of asking the user to type a folder name.
export const fetchBenchmarkInputFolders = async (): Promise<
  CollectionResponse<BenchmarkInputFolders>
> => {
  return axiosInstance
    .get(`/benchmark-input-folders`)
    .then((response) => response.data);
};

export const uploadBenchmarkInputFolder = async (
  inputType: string,
  folderName: string,
  files: FileList | File[],
  onProgress?: (progress: number) => void,
): Promise<BenchmarkInputFolders> => {
  const allowedExtensions =
    inputType === "image_folder"
      ? ["jpg", "jpeg", "png", "webp", "bmp"]
      : inputType === "eml_folder"
        ? ["eml"]
        : [];

  const fileArray = Array.from(files).filter((file) => {
    if (allowedExtensions.length === 0) return true;
    const parts = file.name.split(".");
    if (parts.length < 2) return false;
    const ext = parts.pop()?.toLowerCase() || "";
    return allowedExtensions.includes(ext);
  });

  const CHUNK_SIZE_LIMIT = 60 * 1024 * 1024; // 60 MB to stay safely under 64MB limit
  const CHUNK_FILE_LIMIT = 20; // Stay under PHP's max_file_uploads = 20 limit
  // PHP also caps each *individual* uploaded file via upload_max_filesize (32MB, see
  // docker/php/conf.d/common/customisations.ini) independently of the total request size above.
  // A file over this limit is silently dropped server-side with no error, so it must be rejected here.
  const MAX_SINGLE_FILE_SIZE = 30 * 1024 * 1024; // 30 MB to stay safely under the 32MB PHP limit

  const oversizedFiles = fileArray.filter(
    (file) => file.size > MAX_SINGLE_FILE_SIZE,
  );
  if (oversizedFiles.length > 0) {
    throw new Error(
      `The following files exceed the ${Math.floor(MAX_SINGLE_FILE_SIZE / (1024 * 1024))}MB per-file limit and cannot be uploaded: ${oversizedFiles.map((f) => f.name).join(", ")}`,
    );
  }

  let currentChunkSize = 0;
  let currentChunkFiles: File[] = [];
  const chunks: File[][] = [];

  for (const file of fileArray) {
    if (
      (currentChunkSize + file.size > CHUNK_SIZE_LIMIT ||
        currentChunkFiles.length >= CHUNK_FILE_LIMIT) &&
      currentChunkFiles.length > 0
    ) {
      chunks.push(currentChunkFiles);
      currentChunkFiles = [];
      currentChunkSize = 0;
    }
    currentChunkFiles.push(file);
    currentChunkSize += file.size;
  }
  if (currentChunkFiles.length > 0) {
    chunks.push(currentChunkFiles);
  }

  let lastResponse: BenchmarkInputFolders | null = null;
  let uploadedCount = 0;
  const totalFiles = fileArray.length;

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    const formData = new FormData();
    formData.append("type", inputType);
    formData.append("folderName", folderName);
    // Lets the backend skip rebuilding the full folder listing on every chunk — only the response
    // from the last chunk is ever used below, so intermediate chunks only need to persist files.
    formData.append("isLastChunk", i === chunks.length - 1 ? "1" : "0");

    for (const file of chunk) {
      formData.append("files[]", file);
      formData.append("paths[]", file.webkitRelativePath);
    }

    lastResponse = await axiosInstance
      .post(`/benchmark-input-folders/${inputType}`, formData)
      .then((response) => response.data);

    uploadedCount += chunk.length;
    if (onProgress) {
      // Calculate progress percentage 0-100
      onProgress(Math.round((uploadedCount / totalFiles) * 100));
    }
  }

  if (!lastResponse) {
    throw new Error("No valid files to upload.");
  }

  return lastResponse;
};

export const deleteBenchmarkInputFolder = async (
  inputType: string,
  folderName: string,
): Promise<void> => {
  return axiosInstance.delete(
    `/benchmark-input-folders/${inputType}/${folderName}`,
  );
};

export const renameBenchmarkInputFolder = async (
  inputType: string,
  folderName: string,
  newName: string,
): Promise<BenchmarkInputFolders> => {
  return axiosInstance
    .patch(
      `/benchmark-input-folders/${inputType}/${folderName}`,
      { newName },
      {
        headers: { "Content-Type": "application/merge-patch+json" },
      },
    )
    .then((response) => response.data);
};

export const fetchBenchmarkFolderFiles = async (
  inputType: string,
  folderName: string,
): Promise<string[]> => {
  return axiosInstance
    .get(
      `/benchmark-input-folders/${inputType}/${encodeURIComponent(folderName)}/files`,
    )
    .then((response) => response.data);
};

export const fetchBenchmarkFolderFile = async (
  inputType: string,
  folderName: string,
  filename: string,
): Promise<Blob> => {
  const encodedFilename = encodeURIComponent(filename).replace(/%2F/g, "/"); // preserve slashes
  return axiosInstance
    .get(
      `/benchmark-input-folders/${inputType}/${encodeURIComponent(folderName)}/files/${encodedFilename}`,
      {
        responseType: "blob",
      },
    )
    .then((response) => response.data);
};
